import { logger } from './LoggerService';
import { giveItem } from './ItemGrantService';
import { useBestiaryStore } from '../store/bestiarySlice';
import { MONSTER_CONFIGS } from '../data/monsters.config';
import { saveService } from './SaveService';
import { dungeonService } from './DungeonService';
import { spellExecutor } from './SpellService';
import { SPELL_REGISTRY } from '../data/spells.config';
import { getTargetPosition } from '../utils/map.utils';
import {
  seedAllUnlockedDungeons,
  useUnlockedDungeonsStore,
} from '../store/unlockedDungeonsSlice';
import { useDevtoolStore } from '../store/devtoolSlice';
import { useSpellCatalogStore } from '../store/spellCatalogueSlice';
import { useLoadoutStore } from '../store/loadoutSlice';
import { useNpcStore } from '../store/npcSlice';
import { useHubProgressStore } from '../store/hubProgressSlice';
import { useGameStore } from '../store/gameSlice';
import { hubService } from './HubService';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import { SKETCH_REGISTRY } from '../data/sketches.config';
import { ITEMS } from '../data/inventory.config';
import { NPC_REGISTRY } from '../data/npcs.config';

export function executeCommand(line: string): void {
  const trimmed = line.trim();
  if (!trimmed.startsWith('/')) {
    logger.warn(`Commande invalide : « ${trimmed} » (doit commencer par /)`);
    return;
  }
  const tokens = trimmed.slice(1).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return;
  }
  const [cmd, ...args] = tokens;
  switch (cmd) {
    case 'give': {
      const itemId = args[0];
      const qty = args[1] ? Number(args[1]) : 1;
      if (!itemId || !Number.isFinite(qty) || qty <= 0) {
        logger.warn('Usage : /give <item-id> [quantité]');
        return;
      }
      const overflow = giveItem(itemId, qty);
      logger.info(
        `Give ${itemId} ×${qty}${overflow ? ' (overflow → coffre)' : ''}`,
      );
      return;
    }
    case 'bestiary': {
      const sub = args[0];
      const store = useBestiaryStore.getState();
      if (sub === 'clear') {
        store.resetBestiary();
        logger.info('Bestiary cleared');
        return;
      }
      if (sub === 'seed') {
        const ids = Object.keys(MONSTER_CONFIGS);
        ids.forEach((id, i) => store.recordKill(id, (i % 5) + 1));
        logger.info(`Bestiary seeded: ${ids.length} monsters`);
        return;
      }
      logger.warn('Usage : /bestiary <seed|clear>');
      return;
    }
    case 'cast': {
      const spellId = args[0];
      const player = dungeonService.getPlayer();
      if (!player) {
        logger.warn('Aucun joueur actif (entre dans un donjon).');
        return;
      }
      if (!spellId || spellId === 'list') {
        logger.info(`Sorts : ${Object.keys(SPELL_REGISTRY).join(', ')}`);
        return;
      }
      const def = SPELL_REGISTRY[spellId];
      if (!def) {
        logger.warn(`Sort inconnu : ${spellId} (voir /cast list)`);
        return;
      }
      // Reset the cooldown so a spell can be re-cast on demand while testing.
      player.getSpellState(spellId).cooldownRemaining = 0;
      if (def.parameter.type === 'self') {
        spellExecutor.execute(player, spellId);
      } else {
        const target = getTargetPosition(
          player.positionX,
          player.positionY,
          player.direction,
        );
        spellExecutor.execute(player, spellId, target.x, target.y);
      }
      logger.info(`Cast ${spellId}`);
      return;
    }
    case 'unlock': {
      seedAllUnlockedDungeons();
      logger.info('Tous les donjons sont débloqués.');
      return;
    }
    case 'unlock-all': {
      seedAllUnlockedDungeons();

      const spellCatalog = useSpellCatalogStore.getState();
      Object.keys(SPELL_REGISTRY).forEach((id) => spellCatalog.unlock(id));

      const loadout = useLoadoutStore.getState();
      Object.keys(TRINKET_MAJOR_REGISTRY).forEach((id) =>
        loadout.unlockMajor(id),
      );
      Object.keys(TRINKET_MINOR_REGISTRY).forEach((id) =>
        loadout.unlockMinor(id),
      );
      Object.keys(SKETCH_REGISTRY).forEach((id) => loadout.unlockSketch(id));

      Object.keys(ITEMS).forEach((id) => giveItem(id, 1));

      const npcs = useNpcStore.getState();
      Object.keys(NPC_REGISTRY).forEach((id) => npcs.unlock(id));

      // The full hub ('day' map, all stations) only appears once D1 is completed
      // (HubService.resolveEntryMapId); unlocking alone leaves the player on the
      // reduced 'initial' bedroom. Complete D1, skip its one-time waking-up
      // reveal, and swap the map live if already standing in the hub.
      useUnlockedDungeonsStore.getState().markCompleted('clear-forest');
      useHubProgressStore.getState().setD1RevealPlayed();
      if (useGameStore.getState().gameplay === 'hub') {
        void hubService.switchMap('day');
      }

      logger.info(
        'Tout débloqué : donjons, PNJ (hub complet), sorts, trésors, babioles, croquis, et 1 de chaque objet.',
      );
      return;
    }
    case 'gallery': {
      useDevtoolStore.getState().setGalleryOpen(true);
      logger.info('Galerie de génération ouverte (Échap pour fermer).');
      return;
    }
    case 'save': {
      if (args[0] === 'clear') {
        saveService.clearRunSave();
        logger.info('Run save cleared — next dungeon entry starts fresh.');
        return;
      }
      logger.warn('Usage : /save clear');
      return;
    }
    default:
      logger.warn(`Commande inconnue : /${cmd}`);
  }
}
